import { Component } from '@nicolas-riciotti/light2';

const TOUCH = ('ontouchstart' in window) || (navigator.msMaxTouchPoints > 0);

export default class BlockHeroGrid extends Component {

    attached() {
        this.$spotlight = this.querySelector('[data-spotlight]');
        this.$tint = null; // no separate tint layer anymore
        if (!this.$spotlight) return;

        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.tick = this.tick.bind(this);

        const rect = this.getBoundingClientRect();
        // Final cursor target.
        this.targetX = rect.width  / 2;
        this.targetY = rect.height / 2;
        // Two stacked smoothers — the second eases off the first, so the
        // mask "drags" behind the real cursor like a wet brush stroke.
        this.midX = this.targetX;
        this.midY = this.targetY;
        this.currX = this.targetX;
        this.currY = this.targetY;
        this.lastRenderX = this.currX;
        this.lastRenderY = this.currY;
        this.t0 = performance.now();
        this.noiseSeedA = Math.random() * 1000;
        this.noiseSeedB = Math.random() * 1000;
        this.noiseSeedC = Math.random() * 1000;
        this.noiseSeedD = Math.random() * 1000;
        this.fadeInProgress = 0; // ramps 0→1 once visible
        this._opacity = 0;
        // Read the base radius each layer was rendered with (in px) so we
        // can modulate around it at runtime.
        this.baseR_grid = this.readPxVar(this.$spotlight, '--spot-r');
        this.baseR_tint = this.$tint ? this.readPxVar(this.$tint, '--spot-r') : this.baseR_grid * 1.4;

        this.applyPos();

        this.addEventListener('mousemove', this.onPointerMove);
        this.addEventListener('mouseenter', this.onPointerEnter);
        this.addEventListener('mouseleave', this.onPointerLeave);
        if (TOUCH) {
            this.addEventListener('touchmove', this.onPointerMove, { passive: true });
        }

        this._intersecting = false;
        if ('IntersectionObserver' in window) {
            this._io = new IntersectionObserver((entries) => {
                for (const e of entries) {
                    this._intersecting = e.isIntersecting;
                    if (e.isIntersecting && !this._tickStarted) {
                        this._tickStarted = true;
                        this.fadeInLayers();
                        this._raf = requestAnimationFrame(this.tick);
                    }
                }
            }, { threshold: 0.15 });
            this._io.observe(this);
        } else {
            this._tickStarted = true;
            this.fadeInLayers();
            this._raf = requestAnimationFrame(this.tick);
        }
    }

    fadeInLayers() {
        // Strip the CSS transition so the per-frame opacity modulation in
        // tick() takes over without fighting a transition.
        if (this.$spotlight) this.$spotlight.style.transition = 'none';
    }

    detached() {
        this.removeEventListener('mousemove', this.onPointerMove);
        this.removeEventListener('mouseenter', this.onPointerEnter);
        this.removeEventListener('mouseleave', this.onPointerLeave);
        if (TOUCH) this.removeEventListener('touchmove', this.onPointerMove);
        this._io?.disconnect();
        if (this._raf) cancelAnimationFrame(this._raf);
    }

    onPointerEnter() {
        this.$spotlight.style.opacity = '1';
    }

    onPointerLeave() {
        // Soft fade-out when mouse leaves the hero, ease back to center.
        const rect = this.getBoundingClientRect();
        this.targetX = rect.width  / 2;
        this.targetY = rect.height / 2;
    }

    onPointerMove(event) {
        const touchEvent = TOUCH && event.touches
            ? (event.touches[0] || event.changedTouches[0])
            : event;
        const rect = this.getBoundingClientRect();
        this.targetX = touchEvent.clientX - rect.left;
        this.targetY = touchEvent.clientY - rect.top;
    }

    readPxVar(el, name) {
        const raw = getComputedStyle(el).getPropertyValue(name).trim();
        const num = parseFloat(raw);
        return Number.isFinite(num) ? num : 0;
    }

    applyPos() {
        // Position the mask via CSS variables consumed in the inline
        // `mask-image: radial-gradient(... at var(--mouse-x) var(--mouse-y) ...)`.
        const x = this.currX + 'px';
        const y = this.currY + 'px';
        if (this.$spotlight) {
            this.$spotlight.style.setProperty('--mouse-x', x);
            this.$spotlight.style.setProperty('--mouse-y', y);
        }
        if (this.$tint) {
            this.$tint.style.setProperty('--mouse-x', x);
            this.$tint.style.setProperty('--mouse-y', y);
        }
    }

    tick(now) {
        this._raf = requestAnimationFrame(this.tick);
        if (!this._intersecting) return;

        // Two-stage easing — a touch more drag so the mask trails the
        // cursor like a wet brush stroke.
        const lerpA = 0.12; // pointer → mid
        const lerpB = 0.09; // mid → render
        this.midX += (this.targetX - this.midX) * lerpA;
        this.midY += (this.targetY - this.midY) * lerpA;
        this.currX += (this.midX - this.currX) * lerpB;
        this.currY += (this.midY - this.currY) * lerpB;

        // Modulate radius around the base value so the reveal size varies
        // organically as you move. The feather is kept proportional (same
        // gradient stop %), so visually the softness stays consistent.
        const t = ((now || performance.now()) - this.t0) * 0.001;
        const dx = this.currX - this.lastRenderX;
        const dy = this.currY - this.lastRenderY;
        this.lastRenderX = this.currX;
        this.lastRenderY = this.currY;
        const speed = Math.min(60, Math.sqrt(dx * dx + dy * dy));
        // Two desynced sines + a velocity boost = organic pulse. Higher
        // amplitudes here mean the radius varies more visibly as you move.
        const wobble = Math.sin(t * 0.7 + this.noiseSeedA) * 0.18
                     + Math.sin(t * 1.3 + this.noiseSeedB) * 0.12
                     + (speed / 60) * 0.18;
        this._radiusFactor = (this._radiusFactor || 1) + (1 + wobble - (this._radiusFactor || 1)) * 0.18;

        if (this.$spotlight && this.baseR_grid) {
            this.$spotlight.style.setProperty('--spot-r', (this.baseR_grid * this._radiusFactor) + 'px');
        }
        if (this.$tint && this.baseR_tint) {
            this.$tint.style.setProperty('--spot-r', (this.baseR_tint * this._radiusFactor) + 'px');
        }

        // Fade-in ramp on first appearance, then per-frame opacity wobble
        // so the reveal intensity also varies as you move/breathe.
        this.fadeInProgress = Math.min(1, this.fadeInProgress + 1 / 60);
        const fadeEase = this.fadeInProgress * (2 - this.fadeInProgress); // easeOutQuad
        const opacityWobble = Math.sin(t * 0.55 + this.noiseSeedC) * 0.16
                            + Math.sin(t * 1.15 + this.noiseSeedD) * 0.10
                            + (speed / 60) * 0.18;
        const targetOpacity = fadeEase * Math.max(0.25, Math.min(1, 0.85 + opacityWobble));
        this._opacity += (targetOpacity - this._opacity) * 0.18;
        if (this.$spotlight) this.$spotlight.style.opacity = String(this._opacity);

        this.applyPos();
    }
}

customElements.define('block-hero-grid', BlockHeroGrid);
